import {
  Container,
  Paper,
  Text,
  Title,
  Group,
  Button,
  TextInput,
  Select,
  Table,
  ActionIcon,
  Badge,
  Stack,
} from "@mantine/core";
import { IconPlus, IconSearch, IconEye, IconEdit } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { useRequisitions } from "../hooks/useRequisitions";
import { RequisitionFilters } from "../../../api/requisitions";
import dayjs from "dayjs";

const statusColors: Record<string, string> = {
  DRAFT: "gray",
  PENDING_APPROVAL: "yellow",
  APPROVED: "green",
  REJECTED: "red",
  CONVERTED_TO_RFQ: "blue",
};

const priorityColors: Record<string, string> = {
  LOW: "gray",
  MEDIUM: "blue",
  HIGH: "orange",
  URGENT: "red",
};

export function RequisitionList() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<RequisitionFilters>({
    search: "",
    status: undefined,
    category: undefined,
    priority: undefined,
  });

  const { data: requisitions, isLoading } = useRequisitions(filters);

  const handleFilterChange = (key: string, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  if (isLoading) {
    return (
      <Container size="xl" py="xl">
        <Text>Loading requisitions...</Text>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Group justify="space-between" mb="xl">
        <Title order={2}>Requisitions</Title>
        <Button
          leftSection={<IconPlus size={16} />}
          onClick={() => navigate("/requisitions/new")}
        >
          New Requisition
        </Button>
      </Group>

      <Paper shadow="sm" p="md" mb="xl">
        <Group gap="md">
          <TextInput
            placeholder="Search requisitions..."
            leftSection={<IconSearch size={16} />}
            value={filters.search}
            onChange={(e) => handleFilterChange("search", e.target.value)}
            style={{ flex: 1 }}
          />
          <Select
            placeholder="Status"
            data={[
              { value: "", label: "All Statuses" },
              { value: "DRAFT", label: "Draft" },
              { value: "PENDING_APPROVAL", label: "Pending Approval" },
              { value: "APPROVED", label: "Approved" },
              { value: "REJECTED", label: "Rejected" },
              { value: "CONVERTED_TO_RFQ", label: "Converted to RFQ" },
            ]}
            value={filters.status}
            onChange={(value) => handleFilterChange("status", value || "")}
            style={{ minWidth: 180 }}
          />
          <Select
            placeholder="Category"
            data={[
              { value: "", label: "All Categories" },
              { value: "GOODS", label: "Goods" },
              { value: "SERVICES", label: "Services" },
            ]}
            value={filters.category}
            onChange={(value) => handleFilterChange("category", value || "")}
            style={{ minWidth: 150 }}
          />
          <Select
            placeholder="Priority"
            data={[
              { value: "", label: "All Priorities" },
              { value: "LOW", label: "Low" },
              { value: "MEDIUM", label: "Medium" },
              { value: "HIGH", label: "High" },
              { value: "URGENT", label: "Urgent" },
            ]}
            value={filters.priority}
            onChange={(value) => handleFilterChange("priority", value || "")}
            style={{ minWidth: 150 }}
          />
        </Group>
      </Paper>

      <Paper shadow="sm" p="md">
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Requisition Number</Table.Th>
              <Table.Th>Title</Table.Th>
              <Table.Th>Department</Table.Th>
              <Table.Th>Category</Table.Th>
              <Table.Th>Priority</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Requested By</Table.Th>
              <Table.Th>Created</Table.Th>
              <Table.Th>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {requisitions?.map((req) => (
              <Table.Tr key={req.id}>
                <Table.Td>{req.requisitionNumber}</Table.Td>
                <Table.Td>{req.title}</Table.Td>
                <Table.Td>{req.department}</Table.Td>
                <Table.Td>
                  <Badge variant="light">{req.category}</Badge>
                </Table.Td>
                <Table.Td>
                  <Badge color={priorityColors[req.priority]} variant="light">
                    {req.priority}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Badge color={statusColors[req.status]} variant="light">
                    {req.status.replace("_", " ")}
                  </Badge>
                </Table.Td>
                <Table.Td>{req.requestedBy.fullName}</Table.Td>
                <Table.Td>
                  {dayjs(req.createdAt).format("DD MMM YYYY")}
                </Table.Td>
                <Table.Td>
                  <Group gap="xs">
                    <ActionIcon
                      variant="subtle"
                      onClick={() => navigate(`/requisitions/${req.id}`)}
                    >
                      <IconEye size={16} />
                    </ActionIcon>
                    {req.status === "DRAFT" && (
                      <ActionIcon
                        variant="subtle"
                        onClick={() => navigate(`/requisitions/${req.id}/edit`)}
                      >
                        <IconEdit size={16} />
                      </ActionIcon>
                    )}
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
        {(!requisitions || requisitions.length === 0) && (
          <Stack align="center" py="xl">
            <Text c="dimmed">No requisitions found</Text>
          </Stack>
        )}
      </Paper>
    </Container>
  );
}

